/**
 * User Form Component
 * Reusable form for creating and editing users with multi-step wizard
 */

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Alert,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import {
  UserFormData,
  UserEditFormData,
  UserStatus,
  BindingEntityType,
  BindingFunction,
  USER_STATUS_LABELS,
  BINDING_FUNCTION_LABELS,
  formatCPF,
  formatWhatsApp,
  validateCPF,
} from '../../../types/user';
import { useBindingScope } from '../../../hooks/useBindingScope';
import { getActiveRoles } from '../../../services/roleService';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { Role } from '../../../types/rbac.types';
import BindingEntityField, { BindingEntityValue } from './BindingEntityField';

// Step configuration for create mode
const CREATE_STEPS = [
  { label: 'Dados Básicos', value: 0 },
  { label: 'Senha', value: 1 },
  { label: 'Vínculo Inicial', value: 2 },
];

// Binding function options (using enum values and labels)
const BINDING_FUNCTION_OPTIONS = Object.values(BindingFunction).map((fn) => ({
  value: fn,
  label: BINDING_FUNCTION_LABELS[fn],
}));

// Create user validation schema
const createUserSchema = yup.object({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Mínimo 2 caracteres')
    .max(255, 'Máximo 255 caracteres'),
  email: yup
    .string()
    .required('E-mail é obrigatório')
    .email('E-mail inválido'),
  cpf: yup
    .string()
    .required('CPF é obrigatório')
    .test('cpf-format', 'CPF deve conter 11 dígitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 11;
    })
    .test('cpf-valid', 'CPF inválido', (value) => {
      if (!value) return false;
      return validateCPF(value);
    }),
  password: yup
    .string()
    .notRequired()
    .test('password-strength', 'A senha deve ter pelo menos 8 caracteres com letras maiúsculas, minúsculas e números', (value) => {
      if (!value || value.length === 0) return true;
      if (value.length < 8) return false;
      return /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value);
    }),
  confirmPassword: yup
    .string()
    .notRequired()
    .test('passwords-match', 'As senhas não coincidem', function (value) {
      const { password } = this.parent;
      if (!password || password.length === 0) return true;
      return value === password;
    }),
  whatsapp: yup.string().notRequired(),
  bindingEntityType: yup.string().notRequired(),
  bindingEntityId: yup.string().notRequired(),
  bindingFunction: yup.string().notRequired(),
  roleIds: yup.array().of(yup.number()).notRequired(),
}).required();

// Edit user validation schema
const editUserSchema = yup.object({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Mínimo 2 caracteres')
    .max(255, 'Máximo 255 caracteres'),
  whatsapp: yup.string().notRequired(),
  situacao: yup
    .string()
    .required('Status é obrigatório')
    .oneOf(Object.values(UserStatus), 'Status inválido'),
}).required();

// Common form data type for internal use
type FormData = UserFormData & UserEditFormData;

interface UserFormProps {
  mode: 'create' | 'edit';
  initialData?: Partial<UserFormData> | Partial<UserEditFormData>;
  onSubmit: (data: UserFormData | UserEditFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
}

/** A carga fica fora do componente para não trocar de identidade a cada render. */
const carregarPapeis = async (): Promise<readonly Role[]> => {
  const resposta = await getActiveRoles();

  return resposta.data ?? [];
};

const UserForm: React.FC<UserFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  error,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const isEditMode = mode === 'edit';

  // Use appropriate schema based on mode
  const schema = isEditMode ? editUserSchema : createUserSchema;

  const defaultValues: Partial<FormData> = isEditMode
    ? {
        name: '',
        whatsapp: '',
        situacao: UserStatus.ATIVO,
      }
    : {
        name: '',
        email: '',
        cpf: '',
        password: '',
        confirmPassword: '',
        whatsapp: '',
        bindingEntityType: '' as BindingEntityType,
        bindingEntityId: '',
        bindingFunction: BindingFunction.EMPLOYEE,
        roleIds: [],
      };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm<FormData>({
    resolver: yupResolver(schema as any) as any,
    defaultValues: defaultValues as FormData,
  });

  /**
   * Os papéis disponíveis para marcar na criação.
   *
   * Só na criação: na edição, papel entra e sai na tela de detalhes, onde a mudança vale na
   * hora — um papel marcado num formulário que a pessoa abandona não deveria ter sido concedido.
   *
   * Pelo gancho, e não por `useEffect` cru: a lista pede `role.read`, que a porta da tela de
   * usuários não cobre, e quem não tem essa permissão precisa encontrar campo fechado com o
   * motivo, não um combo vazio.
   */
  const papeis = useListaPermitida<Role>('role.read', 'Papéis', carregarPapeis, {
    habilitado: !isEditMode,
  });
  const papeisDisponiveis = papeis.itens;

  // Load initial data when editing
  useEffect(() => {
    if (initialData) {
      if (isEditMode) {
        const editData = initialData as Partial<UserEditFormData>;
        reset({
          name: editData.name ?? '',
          whatsapp: editData.whatsapp ? formatWhatsApp(editData.whatsapp) : '',
          situacao: editData.situacao || UserStatus.ATIVO,
        } as FormData);
      } else {
        const createData = initialData as Partial<UserFormData>;
        reset({
          name: createData.name ?? '',
          email: createData.email ?? '',
          cpf: createData.cpf ? formatCPF(createData.cpf) : '',
          password: '',
          confirmPassword: '',
          whatsapp: createData.whatsapp ? formatWhatsApp(createData.whatsapp) : '',
          bindingEntityType: createData.bindingEntityType || ('' as BindingEntityType),
          bindingEntityId: createData.bindingEntityId ?? '',
          bindingFunction: createData.bindingFunction || BindingFunction.EMPLOYEE,
        } as FormData);
      }
    }
  }, [initialData, isEditMode, reset]);

  const { entidadeFixa, podeTrocarDeContexto } = useBindingScope();
  const [vincularAgora, setVincularAgora] = useState(true);
  const [entidadeEscolhida, setEntidadeEscolhida] = useState<BindingEntityValue>({
    entityType: '',
    entityId: '',
  });

  // A entidade do vínculo não é digitada: vem do contexto ativo, ou da busca na plataforma.
  const entidadeDoVinculo: BindingEntityValue = entidadeFixa
    ? { entityType: entidadeFixa.entityType, entityId: String(entidadeFixa.entityId) }
    : entidadeEscolhida;

  const rotuloDaFuncao =
    entidadeDoVinculo.entityType === BindingEntityType.PROVIDER
      ? 'Função na clínica'
      : 'Função na empresa';

  // Get fields to validate for current step
  const getStepFields = (stepIndex: number): (keyof FormData)[] => {
    switch (stepIndex) {
      case 0: // Dados Básicos
        return ['name', 'email', 'cpf', 'whatsapp'];
      case 1: // Senha
        return ['password', 'confirmPassword'];
      case 2: // Vínculo Inicial
        return ['bindingEntityType', 'bindingEntityId', 'bindingFunction'];
      default:
        return [];
    }
  };

  // Validate current step before moving to next
  const validateCurrentStep = async (): Promise<boolean> => {
    const fieldsToValidate = getStepFields(currentStep);
    const result = await trigger(fieldsToValidate);
    return result;
  };

  // Handle next button click
  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < CREATE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: FormData) => {
    if (isEditMode) {
      await onSubmit({
        name: data.name,
        whatsapp: data.whatsapp,
        situacao: data.situacao,
      } as UserEditFormData);
    } else {
      const comVinculo = vincularAgora && Boolean(entidadeDoVinculo.entityId);
      await onSubmit({
        ...(data as UserFormData),
        bindingEntityType: comVinculo ? entidadeDoVinculo.entityType : '',
        bindingEntityId: comVinculo ? entidadeDoVinculo.entityId : '',
      } as UserFormData);
    }
  };

  // Handle continue/submit button click
  const handleContinue = async () => {
    if (isEditMode || currentStep === CREATE_STEPS.length - 1) {
      // Last step or edit mode - submit the form
      await handleSubmit(handleFormSubmit)();
    } else {
      // Not last step - go to next step
      await handleNext();
    }
  };

  // Handle CPF input with mask
  const handleCPFChange = (value: string, onChange: (value: string) => void) => {
    const formatted = formatCPF(value);
    onChange(formatted);
  };

  // Handle WhatsApp input with mask
  const handleWhatsAppChange = (value: string, onChange: (value: string) => void) => {
    const formatted = formatWhatsApp(value);
    onChange(formatted);
  };

  // Get error message safely
  const getError = (fieldName: string): string | undefined => {
    const err = (errors as any)[fieldName];
    return err?.message as string | undefined;
  };

  const hasAnyErrors = Object.keys(errors).length > 0;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Stepper - only for create mode */}
      {!isEditMode && (
        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {CREATE_STEPS.map((step) => (
            <Step key={step.value}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {/* Error Alert */}
      {(error || hasAnyErrors) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Por favor, corrija os erros abaixo'}
        </Alert>
      )}
      {/* Step 0: Dados Básicos (Create) or Full form (Edit) */}
      {(currentStep === 0 || isEditMode) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informações Básicas
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nome Completo"
                      required
                      error={!!getError('name')}
                      helperText={getError('name')}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>

              {!isEditMode && (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="email"
                          label="E-mail"
                          required
                          error={!!getError('email')}
                          helperText={getError('email')}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="CPF"
                          required
                          placeholder="XXX.XXX.XXX-XX"
                          error={!!getError('cpf')}
                          helperText={getError('cpf')}
                          disabled={loading}
                          inputProps={{ maxLength: 14 }}
                          onChange={(e) => handleCPFChange(e.target.value, field.onChange)}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid
                size={{
                  xs: 12,
                  md: isEditMode ? 6 : 6
                }}>
                <Controller
                  name="whatsapp"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="WhatsApp"
                      placeholder="(XX) XXXXX-XXXX"
                      error={!!getError('whatsapp')}
                      helperText={getError('whatsapp')}
                      disabled={loading}
                      inputProps={{ maxLength: 15 }}
                      onChange={(e) => handleWhatsAppChange(e.target.value, field.onChange)}
                    />
                  )}
                />
              </Grid>

              {isEditMode && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="situacao"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth required error={!!getError('situacao')}>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status" disabled={loading}>
                          {Object.values(UserStatus).map((status) => (
                            <MenuItem key={status} value={status}>
                              {USER_STATUS_LABELS[status]}
                            </MenuItem>
                          ))}
                        </Select>
                        {getError('situacao') && (
                          <FormHelperText>{getError('situacao')}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Step 1: Senha (Create mode only) */}
      {!isEditMode && currentStep === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Senha
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Deixe em branco para gerar uma senha temporária que será enviada por e-mail.
            </Alert>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      label="Senha (opcional)"
                      error={!!getError('password')}
                      helperText={
                        getError('password') ||
                        'Mínimo 8 caracteres com letras maiúsculas, minúsculas e números'
                      }
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      label="Confirmar Senha"
                      error={!!getError('confirmPassword')}
                      helperText={getError('confirmPassword')}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              disabled={loading}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Step 2: Vínculo Inicial (Create mode only) */}
      {!isEditMode && currentStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vínculo Inicial (opcional)
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              O vínculo é o que dá à pessoa acesso aos dados de uma empresa ou clínica. Pode ser
              feito agora ou depois, na tela dela.
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {entidadeFixa && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={vincularAgora}
                      onChange={(evento) => setVincularAgora(evento.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={`Vincular a ${entidadeFixa.nome || 'esta entidade'} agora`}
                />
              )}

              {(!entidadeFixa || vincularAgora) && (
                <>
                  <BindingEntityField
                    value={entidadeDoVinculo}
                    onChange={setEntidadeEscolhida}
                    entidadeFixa={entidadeFixa}
                    disabled={loading}
                  />

                  <Controller
                    name="bindingFunction"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label={rotuloDaFuncao}
                        disabled={loading}
                        helperText="O que a pessoa faz aqui. Pode ser alterado depois."
                      >
                        {BINDING_FUNCTION_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </>
              )}

              {(papeisDisponiveis.length > 0 || papeis.bloqueio) && (
                <Controller
                  name="roleIds"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? []}
                      select
                      fullWidth
                      label="Papéis (perfis de acesso)"
                      disabled={loading || !papeis.permitido}
                      helperText={papeis.bloqueio ?? 'O que a pessoa pode fazer no sistema. Para apurar denúncias do Canal, marque Comitê de Denúncias.'}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selecionados) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selecionados as number[]).map((id) => {
                              const papel = papeisDisponiveis.find((item) => item.id === id);

                              return (
                                <Chip
                                  key={id}
                                  size="small"
                                  label={papel?.displayName ?? `Papel ${id}`}
                                />
                              );
                            })}
                          </Box>
                        ),
                      }}
                    >
                      {papeisDisponiveis.map((papel) => (
                        <MenuItem key={papel.id} value={papel.id}>
                          {papel.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}

              {entidadeFixa && (
                <Typography variant="caption" color="text.secondary">
                  O vínculo diz que a pessoa é da sua equipe, então ele só vale para a entidade em
                  que você está operando agora.{' '}
                  {podeTrocarDeContexto
                    ? 'Para vincular a outra, troque no seletor do topo da tela.'
                    : 'Para vincular a outra, peça a quem administra os acessos.'}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isEditMode && currentStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<BackIcon />}
              disabled={loading}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode || currentStep === CREATE_STEPS.length - 1 ? (
                <SaveIcon />
              ) : (
                <NextIcon />
              )
            }
            disabled={loading}
          >
            {isEditMode
              ? 'Salvar Alterações'
              : currentStep === CREATE_STEPS.length - 1
              ? 'Criar Usuário'
              : 'Próximo'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default UserForm;
